const WIDTH = 260;
const HEIGHT = 124;
const CENTER = { x: WIDTH / 2, y: 106 };
const BAND = { radius: 92.5, width: 20 };
const TRACK = { radius: 77.75, width: 11.5 };
const TICK = { inner: 78, outer: 83.5, count: 12 };
const BAND_HALF_WIDTH = BAND.width / 2;
const BAND_OUTER_RADIUS = BAND.radius + BAND_HALF_WIDTH;
const LABEL_OFFSET = BAND_OUTER_RADIUS + 6;
const NEEDLE = { hubRadius: 12, length: 58, pinRadius: 4.5 };
const MAX_SCORE = 100;
const DEGREES_PER_POINT = 180 / MAX_SCORE;

const zones = [
    { from: 0, to: 100 / 3, className: 'stroke-background-alert' },
    { from: 100 / 3, to: (100 * 2) / 3, className: 'stroke-background-warning' },
    { from: (100 * 2) / 3, to: 100, className: 'stroke-background-success' },
];

const tickRotations = Array.from({ length: TICK.count - 1 }, (_, index) => ((index + 1) * 180) / TICK.count);

type ArcProps = {
    radius: number;
    width: number;
    className: string;
    from?: number;
    to?: number;
};

// Upper semicircle from score 0 (left) to MAX_SCORE (right); pathLength maps dash units to score points.
const Arc = ({ radius, width, className, from = 0, to = MAX_SCORE }: ArcProps) => (
    <path
        className={className}
        d={`M ${-radius} 0 A ${radius} ${radius} 0 0 1 ${radius} 0`}
        fill="none"
        pathLength={MAX_SCORE}
        strokeDasharray={`${to - from} ${MAX_SCORE}`}
        strokeDashoffset={-from}
        strokeWidth={width}
    />
);

const Dial = () => (
    <>
        <Arc className="stroke-background-gray-ultra-light" {...TRACK} />
        {zones.map(({ from, to, className }) => (
            <Arc key={className} className={className} from={from} to={to} {...BAND} />
        ))}
        {tickRotations.map((rotation) => (
            <line
                key={rotation}
                className="stroke-text-ultra-light"
                transform={`rotate(${rotation})`}
                x1={-TICK.inner}
                x2={-TICK.outer}
            />
        ))}
        <text className="fill-text-ultra-light text-xs" textAnchor="end" x={-LABEL_OFFSET} y={4}>
            0
        </text>
        <text className="fill-text-ultra-light text-xs" textAnchor="start" x={LABEL_OFFSET} y={4}>
            {MAX_SCORE}
        </text>
    </>
);

// Drawn pointing at score 0 (left) and rotated clockwise to the score.
const Needle = ({ score }: { score: number }) => (
    <g transform={`rotate(${score * DEGREES_PER_POINT})`}>
        <path
            className="fill-background-accent"
            d={`M 0 -${NEEDLE.hubRadius} A ${NEEDLE.hubRadius} ${NEEDLE.hubRadius} 0 1 1 0 ${NEEDLE.hubRadius} L -${NEEDLE.length} 1 L -${NEEDLE.length} -1 Z`}
        />
        <circle className="fill-background-default" r={NEEDLE.pinRadius} />
    </g>
);

type Props = {
    /** Score between 0 and 100. */
    score: number;
};

export const TrustScoreGauge = ({ score }: Props) => {
    const clampedScore = Math.min(MAX_SCORE, Math.max(0, score));

    return (
        <div className="flex flex-col items-center">
            <svg aria-hidden="true" height={HEIGHT} viewBox={`0 0 ${WIDTH} ${HEIGHT}`} width={WIDTH}>
                <g transform={`translate(${CENTER.x} ${CENTER.y})`}>
                    <Dial />
                    <Needle score={clampedScore} />
                </g>
            </svg>
            <div className="text-text-light text-lg font-semibold">{`${Math.round(clampedScore)}/${MAX_SCORE}`}</div>
        </div>
    );
};
